import { t } from '../i18n/translations'
import { useLanguage } from '../context/LanguageContext'

const PriestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Head with hat/crown */}
    <circle cx="12" cy="6" r="3" fill="currentColor" />
    <path d="M8 4.5 L12 2 L16 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    {/* Body with vestment */}
    <path d="M7 22 L7 14 C7 11 9 9.5 12 9.5 C15 9.5 17 11 17 14 L17 22" fill="currentColor" />
    {/* Cross on vestment */}
    <line x1="12" y1="12" x2="12" y2="18" stroke="white" strokeWidth="1.5" />
    <line x1="10" y1="14.5" x2="14" y2="14.5" stroke="white" strokeWidth="1.5" />
  </svg>
)

const PeopleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Center person */}
    <circle cx="12" cy="7" r="2.5" fill="currentColor" />
    <path d="M8.5 20 L8.5 14 C8.5 11.8 10 10.5 12 10.5 C14 10.5 15.5 11.8 15.5 14 L15.5 20" fill="currentColor" />
    {/* Left person (smaller, behind) */}
    <circle cx="5.5" cy="9" r="2" fill="currentColor" opacity="0.6" />
    <path d="M3 20 L3 15.5 C3 13.8 4 12 5.5 12 C7 12 8 13.8 8 15.5 L8 20" fill="currentColor" opacity="0.6" />
    {/* Right person (smaller, behind) */}
    <circle cx="18.5" cy="9" r="2" fill="currentColor" opacity="0.6" />
    <path d="M16 20 L16 15.5 C16 13.8 17 12 18.5 12 C20 12 21 13.8 21 15.5 L21 20" fill="currentColor" opacity="0.6" />
  </svg>
)


const ROLE_PATTERNS = [
  // Ge'ez script (፡ is Ge'ez word separator, : is regular colon)
  { pattern: /^ካህን[፡:]\s*/,       role: 'priest',  label: 'ካህን' },
  { pattern: /^ሕዝብ[፡:]\s*/,       role: 'people',  label: 'ሕዝብ' },
  { pattern: /^ዲያቆን[፡:]\s*/,      role: 'deacon',  label: 'ዲያቆን' },
  { pattern: /^በሕብረት[፡:]\s*/,     role: 'people',  label: 'ኵሎሙ' },
  // Latin transliteration
  { pattern: /^[Kk]ahn:\s*/i,   role: 'priest',  label: 'ካህን' },
  { pattern: /^Kahin:\s*/i,     role: 'priest',  label: 'ካህን' },
  { pattern: /^HIZB:\s*/i,      role: 'people',  label: 'ሕዝብ' },
  { pattern: /^Diyaqon:\s*/i,   role: 'deacon',  label: 'ዲያቆን' },
  // English — assistant roles must come before main roles
  { pattern: /^Asst?\.?\s*Priest[,:;\-\s]+/i,  role: 'asstpriest', label: 'ረ.ካህን' },
  { pattern: /^Asst?\.?\s*Deacon[,:;\-\s]+/i,  role: 'deacon', label: 'ረ.ዲያቆን' },
  { pattern: /^Priests?[,:;\-\s]+/i,  role: 'priest',  label: 'ካህን' },
  { pattern: /^Pries[,:;\-\s]+/i,    role: 'priest',  label: 'ካህን' },
  { pattern: /^People[,:;\-\s]+/i,   role: 'people',  label: 'ሕዝብ' },
  { pattern: /^Deacon[,:;\-\s]+/i,   role: 'deacon',  label: 'ዲያቆን' },
  { pattern: /^Together[,:;\-\s]+/i, role: 'people',  label: 'ኵሎሙ' },
]

// Labels follow the globe's app language; role keys and detection stay unchanged.
export function getRoleLabel(role, originalLabel) {
  const key = originalLabel === 'ረ.ዲያቆን' ? 'asstdeacon' : originalLabel === 'ኵሎሙ' ? 'together' : role
  return t('role_' + key)
}

// All valid roles for filtering
export const ROLES = [
  { key: 'all',        label: 'All' },
  { key: 'priest',     label: 'ካህን', en: 'Priest' },
  { key: 'asstpriest', label: 'ረ.ካህን', en: 'Asst. Priest' },
  { key: 'deacon',     label: 'ዲያቆን', en: 'Deacon' },
  { key: 'people',     label: 'ሕዝብ', en: 'People' },
]

/**
 * Assign roles to a list of chants, inheriting from preceding lesson when uncategorized.
 */
export function assignRoles(chants) {
  let lastRole = null
  return chants.map(chant => {
    const detected = detectRole(chant)
    if (detected) {
      lastRole = detected.role
      return { ...chant, _role: detected.role }
    }
    // Inherit from preceding lesson
    return { ...chant, _role: lastRole }
  })
}

// Detect role from a section object, checking multiple text fields
export function detectRole(section) {
  if (!section) return null
  // Check fields in priority order: _geeztext has fewest markers,
  // _englishtext has the most, so check all and use first match
  const fields = ['_geeztext', '_latintext', '_englishtext']
  for (const field of fields) {
    const text = section[field]
    if (!text) continue
    const cleaned = text.replace(/^\+\s*/, '')
    for (const { pattern, role, label } of ROLE_PATTERNS) {
      if (pattern.test(cleaned)) {
        return { role, label }
      }
    }
  }
  return null
}

const ROLE_ICONS = {
  priest: PriestIcon,
  asstpriest: PriestIcon,
  deacon: PriestIcon,
  people: PeopleIcon,
}

export default function RoleIcon({ role, size = 18 }) {
  const Icon = ROLE_ICONS[role]
  if (!Icon) return null
  return (
    <span className={`role-icon role-${role}`}>
      <Icon size={size} />
    </span>
  )
}

export function RoleBadge({ role, label }) {
  useLanguage()
  if (!role) return null
  return (
    <span className={`role-badge role-${role}`}>
      <RoleIcon role={role} size={20} />
      <span className="role-label">{getRoleLabel(role, label)}</span>
    </span>
  )
}
